import disclosureNav from '../../../public/libraries/disclosureNav/js/disclosureNav.js';

export class BuxMenu {
    private options = {
        ariaLabel: "Main",
        breakpointMinWidth: 639,
    };
    private disclosureNavInstance: disclosureNav | undefined = undefined;
    private readonly mobileBreakpoint = 639;

    // DOM Elements
    private readonly buxMenu: HTMLElement | null;
    private readonly buxMenuWrapper: HTMLElement | null;
    private readonly navButton: HTMLButtonElement | null;
    private readonly menuSearch: HTMLElement | null;
    private readonly menuSearchInput: HTMLInputElement | null;
    private readonly menuSearchContainer: HTMLElement | null;
    private readonly menuSearchSubmit: HTMLElement | null;
    private readonly menuSearchBtn: HTMLElement | null;
    private readonly navMenu: HTMLElement | null;
    private readonly mainMenuFirstLink: HTMLElement | null;
    private readonly buxMainMenu: HTMLElement | null;
    private readonly menuLinks: HTMLElement | null;

    constructor() {
        this.buxMenu = document.querySelector(".bux-menu");
        this.buxMenuWrapper = document.querySelector(".bux-menu-wrapper");
        this.navButton = document.querySelector(".disclosure-nav-toggle");
        this.menuSearch = document.querySelector(".bux-menu__search");
        this.menuSearchInput = document.querySelector(".bux-search__input");
        this.menuSearchContainer = document.querySelector(".bux-menu__search");
        this.menuSearchSubmit = document.querySelector(".bux-search__submit");
        this.menuSearchBtn = document.querySelector("#menuSearchBtn");
        this.mainMenuFirstLink = document.querySelector(".bux-menu-wrapper nav > ul > li > a");
        this.buxMainMenu = document.querySelector("#bux-main-menu");
        this.menuLinks = document.querySelector("ul.bux-menu");

        this.navMenu = document.querySelector(".bux-container__menu") || document.querySelector(".bux-container--menu");

        if (this.buxMenu) {
            this.init();
        }
    }

    /**
     * Main Initialization
     */
    private init(): void {
        const sidebarNav = document.querySelectorAll(".bux-container--sidebar-nav__mobile-hidden")[0];
        if (sidebarNav) {
            this.options.breakpointMinWidth = 960;
        }

        this.disclosureNavInstance = new disclosureNav("#bux-main-menu", this.options);
        this.bindMenuLogic();

        this.setupSearch();
        this.mobileEvents();
        this.toggleMobileSearch();

        window.addEventListener("resize", this.handleResize);
        window.addEventListener("load", () => this.toggleMobileSearch());
    }

    /**
     * Logic related to the DisclosureNav interaction (Collision detection, height toggling)
     */
    private bindMenuLogic(): void {
        if (!this.disclosureNavInstance) return;

        this.toggleMenuHeight();
        this.collisionDetection();

        window.addEventListener("resize", () => {
            this.collisionDetection();
            this.toggleMenuHeight();
        });
    }

    private isMenuTooLong(): boolean {
        if (!this.disclosureNavInstance) return false;

        const navElement = this.disclosureNavInstance._navElem as HTMLElement;
        const parentNode = navElement.parentNode as HTMLElement;

        // Calculate Required Space (Width of all list items)
        const menuItems = navElement.querySelectorAll(".disclosure-nav > ul > li");
        let menuItemsTotalWidth = 0;
        for (const item of menuItems) {
            const element = item as HTMLElement;
            const styles = globalThis.getComputedStyle(element);
            const margins = Number.parseFloat(styles.marginLeft) + Number.parseFloat(styles.marginRight);
            menuItemsTotalWidth += element.getBoundingClientRect().width + margins;
        }

        // Calculate Available Space
        const mainContainer = document.querySelector('.bux-container__menu') as HTMLElement;

        let availableSpace: number;
        const menuButtonBuffer = 90;

        if (mainContainer) {
            const containerWidth = mainContainer.getBoundingClientRect().width;
            let siblingsWidth = 0;
            for (const child of mainContainer.children) {
                if (child !== parentNode && child !== navElement && child.id !== 'bux-main-menu') {
                    const style = globalThis.getComputedStyle(child as HTMLElement);
                    if (style.display !== 'none' && style.position !== 'absolute') {
                        siblingsWidth += (child as HTMLElement).getBoundingClientRect().width;
                        siblingsWidth += Number.parseFloat(style.marginLeft) + Number.parseFloat(style.marginRight);
                    }
                }
            }

            const containerStyles = globalThis.getComputedStyle(mainContainer);
            const containerPadding = Number.parseFloat(containerStyles.paddingLeft) + Number.parseFloat(containerStyles.paddingRight);

            availableSpace = containerWidth - siblingsWidth - containerPadding - menuButtonBuffer;
        } else {
            const menuStyles = globalThis.getComputedStyle(parentNode);
            availableSpace = parentNode.getBoundingClientRect().width -
                Number.parseFloat(menuStyles.paddingLeft) -
                Number.parseFloat(menuStyles.paddingRight) -
                menuButtonBuffer;
        }

        if (window.innerWidth <= this.options.breakpointMinWidth) {
            return true;
        }

        return menuItemsTotalWidth >= availableSpace;
    }

    private collisionDetection(): void {
        if (!this.buxMenu || !this.disclosureNavInstance) return;

        const menuIsCompact = this.buxMenu.classList.contains("bux-menu--compact");
        const menuIsTooLong = this.isMenuTooLong();

        if (menuIsTooLong && !menuIsCompact) {
            this.buxMenu.classList.add("bux-menu--compact");
        } else if (!menuIsTooLong && menuIsCompact) {
            this.buxMenu.classList.remove("bux-menu--compact");
        } else if (menuIsTooLong && menuIsCompact) {
            this.disclosureNavInstance.addMenuToggle();
            if (this.disclosureNavInstance._options.orientation === "horizontal") {
                this.disclosureNavInstance.switchOrientation();
            }
        }
    }

    /**
     * Utilities
     */
    private isSafariMobile(): boolean {
        return /iPad|iPhone|iPod/.test(navigator.userAgent);
    }

    private toggleMenuHeight(): void {
        let bottomProperty: 'marginBottom' | 'paddingBottom' = "marginBottom";
        if (this.isSafariMobile()) {
            bottomProperty = "paddingBottom";
        }

        if (this.navButton) {
            this.navButton.setAttribute("type", "button");

            this.navButton.addEventListener('click', () => {
                if (!this.buxMenuWrapper || !this.buxMainMenu) return;

                if (this.navButton?.getAttribute("aria-expanded") === "false") {
                    (this.buxMenuWrapper.style)[bottomProperty] = "0px";
                } else {
                    const menuHeight = this.buxMainMenu.clientHeight;
                    (this.buxMenuWrapper.style)[bottomProperty] = `${menuHeight}px`;
                }
            });

            const subLevel = document.querySelectorAll(".disclosure-nav-top-level");
            for (const subButton of subLevel) {
                subButton.addEventListener("click", () => {
                    if (this.buxMainMenu && this.buxMenuWrapper) {
                        const menuHeight = this.buxMainMenu.clientHeight;
                        (this.buxMenuWrapper.style)[bottomProperty] = `${menuHeight}px`;
                    }
                });
            }
        }

        if (
            this.buxMainMenu?.classList.contains("disclosure-nav-responsive") &&
            this.buxMenuWrapper?.style.marginBottom
        ) {
            (this.buxMenuWrapper.style)[bottomProperty] = "0px";
        }
    }

    /**
     * Search Functionality
     */
    private setupSearch(): void {
        if (!this.menuSearch || !this.menuSearchBtn || !this.menuSearchInput || !this.menuSearchContainer || !this.menuSearchSubmit) return;

        this.menuSearchBtn.addEventListener('click', () => this.toggleSearch());

        const mainMenuToggle = document.querySelector("#main-menu-toggle");
        if (mainMenuToggle) {
            mainMenuToggle.addEventListener('click', () => this.toggleSearch());
        }

        // Keyboard Accessibility
        this.menuSearchInput.addEventListener('keydown', (event) => this.handleSearchKeydown(event, this.menuSearchBtn!, this.menuSearchSubmit!));
        this.menuSearchBtn.addEventListener('keydown', (event) => this.handleSearchKeydown(event, this.menuSearchInput!, this.menuSearchSubmit!));
        this.menuSearchSubmit.addEventListener('keydown', (event) => this.handleSearchKeydown(event, this.menuSearchInput!, this.menuSearchBtn!));

        document.addEventListener("click", (event) => {
            const target = event.target as Node;
            const isClickInside =
                this.menuSearchInput?.contains(target) ||
                this.menuSearchSubmit?.contains(target) ||
                this.menuSearchBtn?.contains(target);

            if (!isClickInside && this.menuSearchContainer?.classList.contains("active")) {
                this.toggleSearch();
            }
        });

        this.menuSearchInput.addEventListener("focus", () => {
            this.pauseEventListeners(globalThis, "resize", this.handleResize, 100);
        });
    }

    private handleSearchKeydown(event: KeyboardEvent, nextElement: HTMLElement, previousElement: HTMLElement): void {
        if (!this.menuSearchContainer?.classList.contains("active")) return;

        if (event.key === "Tab") {
            if (event.shiftKey) {
                event.preventDefault();
                previousElement.focus();
            } else {
                event.preventDefault();
                nextElement.focus();
            }
        } else if (event.key === "Escape") {
            event.preventDefault();
            this.toggleSearch();
            this.menuSearchBtn?.focus();
        }
    }

    private toggleSearch(): void {
        if (!this.menuSearchContainer || !this.menuSearchInput || !this.menuSearchSubmit || !this.menuSearchBtn) return;

        const searchIcon = document.querySelector("#searchIcon");
        this.menuSearchContainer.classList.toggle("active");

        requestAnimationFrame(() => this.menuSearchInput?.focus());

        if (this.menuSearchInput.getAttribute("tabindex") === "0") {
            this.menuSearchInput.setAttribute("tabindex", "-1");
            this.menuSearchBtn.setAttribute("aria-expanded", "false");
        } else {
            this.menuSearchInput.setAttribute("tabindex", "0");
            this.menuSearchBtn.setAttribute("aria-expanded", "true");
        }

        this.menuSearchSubmit.classList.toggle("is-hidden");
        const currentTabIndex = this.menuSearchSubmit.getAttribute("tabindex");
        this.menuSearchSubmit.setAttribute("tabindex", currentTabIndex === "0" ? "-1" : "0");

        if (searchIcon) searchIcon.classList.toggle("icon-xmark");

        if (this.mainMenuFirstLink) {
            const linkTabIndex = this.menuSearchContainer.classList.contains("active") ? "-1" : "0";
            this.mainMenuFirstLink.setAttribute("tabindex", linkTabIndex);
        }
    }

    /**
     * Mobile Specific Logic
     */
    private toggleMobileSearch(): void {
        if (!this.menuSearch || !this.menuSearchContainer || !this.menuSearchInput || !this.menuSearchSubmit) return;

        const windowWidth = window.innerWidth;
        const isMenuMobile = this.buxMainMenu?.classList.contains("disclosure-nav-responsive") || false;
        const isActive = this.menuSearchContainer.classList.contains("active");

        if (windowWidth <= this.mobileBreakpoint) {
            this.menuSearchSubmit.classList.remove("is-hidden");
            this.menuSearchInput.setAttribute("tabindex", "0");
            this.menuSearchSubmit.setAttribute("tabindex", "0");
        } else {
            if (isActive) {
                this.menuSearchSubmit.classList.remove("is-hidden");
                this.menuSearchInput.setAttribute("tabindex", "0");
                this.menuSearchSubmit.setAttribute("tabindex", "0");
            } else {
                this.menuSearchSubmit.classList.add("is-hidden");
                this.menuSearchInput.setAttribute("tabindex", "-1");
                this.menuSearchSubmit.setAttribute("tabindex", "-1");
            }
        }

        if (windowWidth > this.mobileBreakpoint && isMenuMobile) {
            this.menuSearch.classList.add("bux-menu--mobile");
            this.menuSearchInput.setAttribute("tabindex", "0");
            this.menuSearchSubmit.setAttribute("tabindex", "0");
            this.menuSearchSubmit.classList.remove("is-hidden");
        } else if (windowWidth > this.mobileBreakpoint && !isMenuMobile) {
            this.menuSearch.classList.remove("bux-menu--mobile");
            this.menuSearchSubmit.classList.add("is-hidden");
        }
    }

    private moveSearchToNav(): void {
        if (this.menuSearch && this.buxMainMenu && this.menuLinks) {
            this.buxMainMenu.insertBefore(this.menuSearch, this.menuLinks);
        }
    }

    private moveSearchBack(): void {
        if (this.menuSearch && this.navMenu && this.buxMainMenu) {
            this.navMenu.insertBefore(this.menuSearch, this.buxMainMenu.nextSibling);
        }
    }

    private handleFocusTrap = (event: KeyboardEvent): void => {
        if (event.key !== "Tab") return;
        if (!this.navMenu) return;

        const navLinks = [...this.navMenu.querySelectorAll(".bux-menu__item > a")] as HTMLElement[];

        if (event.target === navLinks.at(-1) && !event.shiftKey) {
            event.preventDefault();
            this.navButton?.focus();
        }
    }

    private mobileEvents(): void {
        const isMobile = this.buxMainMenu?.classList.contains("disclosure-nav-responsive") || false;

        if (isMobile) {
            this.moveSearchToNav();
            if (this.navButton && this.navMenu) {
                this.navButton.addEventListener('click', () => {
                    const expanded = this.navButton!.getAttribute("aria-expanded") === "true";
                    this.navMenu!.setAttribute("aria-modal", expanded ? "false" : "true");
                });
            }
            this.navMenu?.addEventListener("keydown", this.handleFocusTrap);
        } else {
            this.moveSearchBack();
            this.navMenu?.removeEventListener("keydown", this.handleFocusTrap);
            if (this.menuSearchInput) {
                this.menuSearchInput.setAttribute("tabindex", "-1");
            }
        }
    }

    private handleResize = (): void => {
        if (!document.activeElement || document.activeElement !== this.menuSearchInput) {
            this.mobileEvents();
            this.toggleMobileSearch();
        }
    }

    private pauseEventListeners(
        element: EventTarget,
        eventType: string,
        handler: EventListenerOrEventListenerObject,
        duration: number
    ): void {
        element.removeEventListener(eventType, handler);
        setTimeout(() => {
            element.addEventListener(eventType, handler);
        }, duration);
    }
}

export const init = () => {
    if (document.querySelector(".bux-menu")) {
        new BuxMenu();
    }
}

if (document.readyState === 'loading') {
    document.addEventListener("DOMContentLoaded", init);
} else {
    init();
}